import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import {
  getBaseRequestData,
  isSuccessfulResponse,
} from '../../../utils/evergentUtils';
import { ConfigService } from '../../config.service';
import { LocalStorageService } from '../../storage/local-storage.service';
import { GetParentalControlLevelsReqMessage } from './model/get-parental-control-level.request';
import { GetParentalControlLevelsRespMessage } from './model/get-parental-control-level.response';
import { ValidateParentalControlPINRequestMessage } from './model/validate-parental-control.request';
import { ValidateParentalControlPinModel } from './model/validate-parental-control.response';
import { KEYS } from '../../../utils/constants/storage.constants';
import { getApiUrl } from 'src/app/shared/utils/apiUtils';
import { ResourceVm } from 'qp-ui-interface';
import { isLoggedIn } from 'src/app/shared/utils/LoginUtils';
import { AccountProfileService } from '../account/account-profile.service';
import { ContactMessageModel } from '../onboarding/model/get-profiles/get-contact-response.model';
import {
  getAllowLevels,
  isWatchableContent,
} from 'src/app/shared/utils/ParentalPinUtils';
import { AuthService } from '../../auth/auth.service';
// Removed import of ParentalPinComponent to break circular dependency

@Injectable({
  providedIn: 'root',
})
export class ParentalControlService {
  /** UMS API URL */
  private umsUrl: string;

  /** Cached parental control levels response */
  private levelsResponse: GetParentalControlLevelsRespMessage | undefined;
  resource: ResourceVm;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private localStorage: LocalStorageService,
    private accountProfileService: AccountProfileService,
    private authService: AuthService
  ) {
    this.umsUrl = this.configService.applicationConfig.UmsURL;
    this.initializeParentalControlLevels();
  }

  /**
   * Initializes parental control levels by fetching them from the server
   */
  private initializeParentalControlLevels(): void {
    if (!this.levelsResponse && this.authService.isLoggedIn()) {
      this.fetchParentalControlLevels()
        .toPromise()
        .then((res) => {
          this.levelsResponse = res;
        })
        .catch((error) => {
          console.error('Error initializing parental control levels:', error);
        });
    }
  }

  /**
   * Validates a parental control PIN
   * @param parentalControlPin - The PIN to validate
   * @returns Observable<ValidateParentalControlPinModel> - Response from the validation
   */
  validateParentalControlPin(
    parentalControlPin: string
  ): Observable<ValidateParentalControlPinModel> {
    const url = getApiUrl(this.umsUrl, 'VALIDATE_PARENTAL_CONTROL_PIN');
    const requestMessage = this.createValidatePinRequest(parentalControlPin);

    return this.http
      .post<ValidateParentalControlPinModel>(url, requestMessage, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Creates a validate PIN request message
   * @param parentalControlPin - The PIN to validate
   * @returns ValidateParentalControlPINRequestMessage - The formatted request message
   */
  private createValidatePinRequest(
    parentalControlPin: string
  ): ValidateParentalControlPINRequestMessage {
    const requestMessage = new ValidateParentalControlPINRequestMessage();
    requestMessage.ValidateParentalControlPINRequestMessage = {
      ...getBaseRequestData(this.configService.applicationConfig),
      parentalControlPin,
    };
    return requestMessage;
  }

  async checkParentalLevels(resource: ResourceVm): Promise<boolean> {
    this.resource = resource;
    if (isLoggedIn()) {
      const contactProfileResponse = await this.accountProfileService
        .getContactProfile(this.localStorage.getItem(KEYS.SELECTED_PROFILE_ID))
        .pipe(take(1))
        .toPromise();

      if (
        isSuccessfulResponse(contactProfileResponse.GetContactResponseMessage)
      ) {
        return this.handleParentalControl(
          contactProfileResponse.GetContactResponseMessage.contactMessage[0]
        );
      }
      return Promise.resolve(true);
    } else {
      return Promise.resolve(true);
    }
  }

  async handleParentalControl(
    contactInfo: ContactMessageModel
  ): Promise<boolean> {
    const ageGroup = contactInfo ? contactInfo.ageGroup.replace(/\s/g, '') : '';
    if (!contactInfo.parentalControl || !ageGroup || ageGroup === '') {
      return Promise.resolve(true);
    } else {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { GetParentalControlLevelsRespMessage } =
        await this.getParentalControlLevels();
      const allowedLevels = getAllowLevels(
        GetParentalControlLevelsRespMessage.parentalControlLevels,
        ageGroup
      );
      let isWatchable = isWatchableContent(allowedLevels, this.resource);
      if (this.resource.ratings) {
        if (!isWatchable) {
          return Promise.resolve(false);
        }
      }
      return Promise.resolve(true);
    }
  }
  /**
   * Fetches parental control levels from the server
   * @returns Observable<GetParentalControlLevelsRespMessage> - Response containing control levels
   */
  // To Do : Check this method is really needed ?
  fetchParentalControlLevels(): Observable<GetParentalControlLevelsRespMessage> {
    const url = `${this.umsUrl}/aha/getParentalControlLevels`;
    const requestMessage = this.createGetLevelsRequest();

    return this.http
      .post<GetParentalControlLevelsRespMessage>(url, requestMessage, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Creates a get levels request message
   * @returns GetParentalControlLevelsReqMessage - The formatted request message
   */
  private createGetLevelsRequest(): GetParentalControlLevelsReqMessage {
    const requestMessage = new GetParentalControlLevelsReqMessage();
    requestMessage.GetParentalControlLevelsReqMessage = {
      ...getBaseRequestData(this.configService.applicationConfig),
    };
    return requestMessage;
  }

  /**
   * Gets parental control levels, optionally forcing a refresh
   * @param force - Whether to force a refresh from the server
   * @returns Promise<GetParentalControlLevelsRespMessage> - Response containing control levels
   */
  getParentalControlLevels(
    force?: boolean
  ): Promise<GetParentalControlLevelsRespMessage> {
    if (!this.levelsResponse || force) {
      return this.fetchParentalControlLevels().toPromise();
    }
    return Promise.resolve(this.levelsResponse);
  }

  /**
   * Creates HTTP headers with authorization
   * @returns HttpHeaders - Headers for API requests
   */
  private getHeaders(): HttpHeaders {
    const token = this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN);
    return new HttpHeaders({
      Authorization: `Bearer ${token === 'undefined' ? '' : token}`,
    });
  }

  /**
   * Handles HTTP errors
   * @param error - The HTTP error response
   * @returns Observable<never> - Error observable
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Backend returned code ${error.status}, body was: ${error.error}`;
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}
